import _ from 'lodash';
import { Reducer } from 'redux';
import { IBenchmark } from '../models';
import * as BenchmarkLibraryAPI from '../services/BenchmarkLibraryAPI';
import { IApplicationState } from './';
import { ActionTypes } from './ActionTypes';
import {
	IBenchmarkGetFailed,
	IBenchmarkGetMissingSuccessful,
	IBenchmarkGetRequested,
	IBenchmarkGetSuccessful,
	IBenchmarkGlobalTopQuartileId,
} from './Actions';

export interface IBenchmarksState {
	benchmarksLoading: boolean;
	availableBenchmarks: IBenchmark[];
	globalTopQuartileId?: number;
}

export const defaultState: IBenchmarksState = {
	benchmarksLoading: false,
	availableBenchmarks: [],
};

const getBenchmarksInternal = (dispatch: any, getState: () => IApplicationState) => {
	dispatch({ type: ActionTypes.BENCHMARKS_GET_REQUESTED });
	const clientId = getState().oidc.user?.profile.external_clientid;
	const response = BenchmarkLibraryAPI.getClientBenchmarks(clientId);

	return response
		.then((benchmarks: IBenchmark[]) => {
			dispatch({ type: ActionTypes.BENCHMARKS_GET_SUCCESSFUL, payload: benchmarks });
		})
		.catch(() => dispatch({ type: ActionTypes.BENCHMARKS_GET_FAILED }));
};

export const ActionCreators = {
	GetBenchmarks: () => getBenchmarksInternal,
	GetBenchmarksIfNeeded: () => (dispatch: any, getState: () => IApplicationState) => {
		if (!getState().benchmarks.availableBenchmarks.length) {
			return getBenchmarksInternal(dispatch, getState);
		}
	},
	GetMissingBenchmarks: (missingIds: number[]) => (dispatch: any) => {
		return BenchmarkLibraryAPI.getBenchmarksByIds(missingIds).then((additionalBenchmarks: IBenchmark[]) => {
			dispatch({ type: ActionTypes.BENCHMARKS_GET_MISSING_SUCCESSFUL, payload: additionalBenchmarks });
		});
	},
	GetGlobalTopQuartileId: () => (dispatch: any, getState: () => IApplicationState) => {
		if (getState().benchmarks.globalTopQuartileId === undefined) {
			return BenchmarkLibraryAPI.getGlobalTopQuartileId().then((id: number) =>
				dispatch({ type: ActionTypes.BENCHMARK_GET_GLOBALTOPQUARTILEID, payload: id }),
			);
		}
	},
};

type KnownActions =
	| IBenchmarkGetFailed
	| IBenchmarkGetRequested
	| IBenchmarkGetSuccessful
	| IBenchmarkGetMissingSuccessful
	| IBenchmarkGlobalTopQuartileId;

export const reducer: Reducer<IBenchmarksState, KnownActions> = (
	state: IBenchmarksState | undefined,
	action: KnownActions,
): IBenchmarksState => {
	if (!state) {
		return defaultState;
	}
	switch (action.type) {
		// BENCHMARKS
		case ActionTypes.BENCHMARKS_GET_REQUESTED:
			return {
				...state,
				benchmarksLoading: true,
			};
		case ActionTypes.BENCHMARKS_GET_FAILED:
			return {
				...state,
				availableBenchmarks: [],
				benchmarksLoading: false,
			};
		case ActionTypes.BENCHMARKS_GET_SUCCESSFUL:
			return {
				...state,
				benchmarksLoading: false,
				availableBenchmarks: _.orderBy(action.payload, ['name', 'code']),
			};
		case ActionTypes.BENCHMARKS_GET_MISSING_SUCCESSFUL: {
			const newBenchmarks = action.payload.filter((b) => !state.availableBenchmarks.find((ab) => ab.benchmarkId === b.benchmarkId));
			const combinedBenchmarks = [...state.availableBenchmarks].concat(_.orderBy(newBenchmarks, ['name', 'code']));
			return {
				...state,
				benchmarksLoading: false,
				availableBenchmarks: combinedBenchmarks,
			};
		}
		case ActionTypes.BENCHMARK_GET_GLOBALTOPQUARTILEID:
			return { ...state, globalTopQuartileId: action.payload };
		default:
			return { ...state };
	}
};
